import { buildTagTreeRows } from '@zhiyue/task-management';
import type {
  Tag,
  TaskBoardWithTasks,
  TaskList,
  TaskNavPendingCounts,
  TaskStatistics,
} from '../apis';
import { tagApi, taskApi, taskBoardApi, taskListApi } from '../apis';
import { menuConfigApi } from '../apis/menu-config';
import type { Menu } from '../types/menu';
import {
  buildMenuTree,
  buildProMenuData,
  findBestSelectedKey,
  findMenuAncestorKeys,
  type MenuTreeNode,
  normalizeMenus,
  type ProMenuDataItem,
} from './build-menu-tree';

type Translate = (key: string) => string;

export interface TaskNavState {
  initialized: boolean;
  loading: boolean;
  menus: Menu[];
  menuTree: MenuTreeNode[];
  selectedKey: string;
  openKeys: string[];
  taskCont: TaskStatistics | null;
  navPendingCounts: TaskNavPendingCounts;
  tags: Tag[];
  taskBoards: TaskBoardWithTasks[];
  taskLists: TaskList[];
}

const DEFAULT_OPEN_KEYS: string[] = [];
const RESOURCE_MENU_TARGETS: Record<string, string> = {
  '/task/screen': '/task/screen/all',
  '/task/screen/all': '/task/screen/all',
  '/task/task-lists': '/task/task-lists/visible',
  '/task/task-lists/visible': '/task/task-lists/visible',
  '/task/tags': '/task/tags/visible',
  '/task/tags/visible': '/task/tags/visible',
};
const DEFAULT_NAV_PENDING_COUNTS: TaskNavPendingCounts = {
  assignee: 0,
  today: 0,
  week: 0,
  next7Days: 0,
  allTask: 0,
  create: 0,
  assigner: 0,
  watched: 0,
};

let state: TaskNavState = {
  initialized: false,
  loading: false,
  menus: [],
  menuTree: [],
  selectedKey: '',
  openKeys: DEFAULT_OPEN_KEYS,
  taskCont: null,
  navPendingCounts: DEFAULT_NAV_PENDING_COUNTS,
  tags: [],
  taskBoards: [],
  taskLists: [],
};

let initPromise: Promise<void> | null = null;
let navPendingCountsPromise: Promise<void> | null = null;
let translate: Translate = (key) => key;
const listeners = new Set<() => void>();

const emit = () => {
  listeners.forEach((listener) => {
    listener();
  });
};

const setState = (patch: Partial<TaskNavState>) => {
  state = {
    ...state,
    ...patch,
  };
  emit();
};

const rebuildMenus = () => {
  let generatedId = -100000;
  const tagNodes = (
    rows: ReturnType<typeof buildTagTreeRows<Tag>>,
  ): MenuTreeNode[] =>
    rows.map((tag) => {
      const children = tagNodes(tag.children || []);
      const hasChildren = children.length > 0;
      const route = `/task/tags/${tag.uuid}`;
      return {
        id: generatedId--,
        name: tag.name.split('/').pop() || tag.name,
        menu_type: hasChildren ? 'submenu' : 'item',
        route_path: tag.isTagGroup
          ? tag.uuid
          : hasChildren
            ? `__tag_branch__:${tag.uuid}`
            : route,
        children: hasChildren
          ? [
              ...(!tag.isTagGroup
                ? [
                    {
                      id: generatedId--,
                      name: '查看全部',
                      menu_type: 'item' as const,
                      route_path: route,
                    },
                  ]
                : []),
              ...children,
            ]
          : undefined,
      };
    });
  const menusWithChildren = state.menus.map((item) => {
    const targetRoute = item.route_path
      ? RESOURCE_MENU_TARGETS[item.route_path]
      : undefined;
    if (!targetRoute) return item;
    if (targetRoute === '/task/tags/visible') {
      return {
        ...item,
        route_path: '/task/tags',
        menu_type: 'submenu' as const,
        children: [
          {
            id: generatedId--,
            name: '全部标签',
            menu_type: 'item' as const,
            route_path: targetRoute,
          },
          ...tagNodes(buildTagTreeRows(state.tags)),
        ],
      };
    }
    return {
      ...item,
      route_path: targetRoute,
      menu_type: 'item' as const,
      children: undefined,
    };
  });
  const menuTree = buildMenuTree(menusWithChildren);
  setState({
    menuTree,
    openKeys: [
      ...new Set([
        ...state.openKeys,
        ...findMenuAncestorKeys(state.selectedKey, menuTree),
      ]),
    ],
  });
};

const fetchTaskStatistics = async () => {
  try {
    const taskCont = await taskApi.getTaskStatistics();
    setState({ taskCont });
  } catch (error) {
    console.error('加载任务统计失败:', error);
    setState({ taskCont: null });
  }
};

const fetchNavPendingCounts = async () => {
  if (navPendingCountsPromise) {
    return navPendingCountsPromise;
  }

  navPendingCountsPromise = (async () => {
    try {
      const navPendingCounts = await taskApi.getNavPendingCounts();
      setState({ navPendingCounts });
    } catch (error) {
      console.error('加载导航未完成统计失败:', error);
    } finally {
      navPendingCountsPromise = null;
    }
  })();

  return navPendingCountsPromise;
};

const fetchTaskLists = async () => {
  try {
    const response = await taskListApi.getTaskLists();
    const allTaskLists: TaskList[] = response.taskLists || [];

    const uniqTaskLists = Array.from(
      new Map(allTaskLists.map((item) => [item.uuid, item])).values(),
    );
    setState({ taskLists: uniqTaskLists });
  } catch (error) {
    console.error('加载清单列表失败:', error);
    setState({ taskLists: [] });
  }
};

const fetchTags = async () => {
  try {
    const response = await tagApi.getTags();
    const allTags: Tag[] = response.tags || [];

    const uniqTags = Array.from(
      new Map(allTags.map((item) => [item.uuid, item])).values(),
    );
    setState({ tags: uniqTags });
  } catch (error) {
    console.error('加载标签列表失败:', error);
    setState({ tags: [] });
  }
};

const fetchBoards = async () => {
  try {
    const response = await taskBoardApi.getTaskBoardsWithTasks(100);
    setState({ taskBoards: response || [] });
  } catch (error) {
    console.error('加载大屏列表失败:', error);
    setState({ taskBoards: [] });
  }
};

const loadMenus = async () => {
  let menus: Menu[] = [];

  try {
    const response = await menuConfigApi.getMenuList();
    menus = response.menus || [];
  } catch (error) {
    console.error('加载菜单配置失败，已使用默认菜单兜底:', error);
    menus = menuConfigApi.getDefaultMenus();
  }

  if (!menus.length) {
    menus = menuConfigApi.getDefaultMenus();
  }

  const normalized = normalizeMenus(menus);
  setState({
    menus: normalized,
    menuTree: buildMenuTree(normalized),
  });
};

export const taskNavStore = {
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  getSnapshot(): TaskNavState {
    return state;
  },
  async initOnce(t: Translate): Promise<void> {
    translate = t;
    if (state.initialized) return;
    if (!initPromise) {
      initPromise = (async () => {
        setState({ loading: true });
        try {
          await loadMenus();
          await Promise.all([
            fetchTaskStatistics(),
            fetchNavPendingCounts(),
            fetchTags(),
            fetchTaskLists(),
            fetchBoards(),
          ]);
          rebuildMenus();
          setState({ initialized: true });
        } finally {
          setState({ loading: false });
        }
      })();
    }
    await initPromise;
  },
  async reloadMenus(t?: Translate): Promise<void> {
    if (t) translate = t;
    setState({ loading: true });
    try {
      await loadMenus();
      rebuildMenus();
    } finally {
      setState({ loading: false });
    }
  },
  async refreshMenuData(t?: Translate): Promise<void> {
    if (t) translate = t;
    await Promise.all([
      fetchTaskStatistics(),
      fetchNavPendingCounts(),
      fetchTags(),
      fetchTaskLists(),
      fetchBoards(),
    ]);
    rebuildMenus();
  },
  async loadTags(t?: Translate): Promise<void> {
    if (t) translate = t;
    await fetchTags();
    rebuildMenus();
  },
  async loadBoards(t?: Translate): Promise<void> {
    if (t) translate = t;
    await fetchBoards();
    rebuildMenus();
  },
  async loadTaskLists(t?: Translate): Promise<void> {
    if (t) translate = t;
    await fetchTaskLists();
    rebuildMenus();
  },
  async updataTaskCont(): Promise<void> {
    await Promise.all([fetchTaskStatistics(), fetchNavPendingCounts()]);
  },
  async refreshNavPendingCounts(): Promise<void> {
    await fetchNavPendingCounts();
  },
  setSelectedKey(selectedKey: string) {
    if (state.selectedKey === selectedKey) return;
    setState({
      selectedKey,
      openKeys: [
        ...new Set([
          ...state.openKeys,
          ...findMenuAncestorKeys(selectedKey, state.menuTree),
        ]),
      ],
    });
  },
  resolveSelectedKey(pathname: string) {
    return findBestSelectedKey(pathname, state.menuTree);
  },
  setOpenKeys(openKeys: string[]) {
    setState({ openKeys });
  },
  toProLayoutMenuData(t?: Translate): ProMenuDataItem[] {
    if (t) translate = t;
    return buildProMenuData(state.menuTree, translate);
  },
  getTagById(tagId?: string) {
    if (!tagId) return undefined;
    return state.tags.find((tag) => tag.uuid === tagId);
  },
};
